import React from "react";
import { Link } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import SEO from "../components/SEO";

export default function BlogPostFugtmaaling() {
  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title="Fugtmåling: Hvad kan en måling vise – og hvad kan den ikke?"
        description="Hvornår giver en fugtmåling mening, og hvad er forskellen på ikke-destruktiv måling, nålemåling og RF-måling i borehuller? Få overblikket her."
        canonical="https://www.etdanmark.com/blog/fugtmaaling-hvad-kan-den-vise"
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07589.webp"
        ogType="article"
      />

      {/* TOP HEADER */}
      <PageTopSlider
        title="Fugtmåling: Hvad kan en måling vise – og hvad kan den ikke?"
        subtitle="Hvornår giver en fugtmåling mening, og hvad er forskellen på ikke-destruktiv scanning, nålemåling og RF-måling i borehuller? Få et sagligt overblik fra energikonsulenten."
        badge="Faglig guide & viden"
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Fugtmåling", to: "/fugtmaaling" },
          { label: "Hvad kan en fugtmåling vise?" },
        ]}
        actions={
          <Link
            to="/fugtmaaling"
            className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
          >
            <i className="fa-solid fa-droplet text-xs" aria-hidden="true"></i>
            <span>Få et uforpligtende tilbud på fugtmåling</span>
          </Link>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg",
          alt: "Fugtmåling i bygning med Tramex udstyr",
        }}
      />

      {/* ARTIKEL INDHOLD */}
      <article className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Metadata forfatter */}
          <div className="flex flex-wrap items-center gap-4 pb-8 mb-10 border-b border-stone-200 text-xs text-stone-500">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#165B4C] text-white flex items-center justify-center font-bold text-xs">
                LL
              </div>
              <span className="font-semibold text-stone-800">
                Lars Lauritzen, ET Danmark
              </span>
            </div>
            <span>•</span>
            <span>Bygningssagkyndig rådgivning</span>
            <span>•</span>
            <span>Opdateret forår 2026</span>
          </div>

          <div className="prose prose-stone max-w-none space-y-8 text-stone-700 text-base leading-relaxed">
            <p className="text-lg font-medium text-stone-900 leading-relaxed">
              Fugt er en af de mest almindelige årsager til skader i bygninger. Problemet er, at fugten sjældent er synlig, før den har gjort skade – i gulvet, bag en væg eller under tagpappen. En fugtmåling gør det usynlige målbart. Men der findes flere slags målinger, og de kan ikke det samme. Her får du overblikket.
            </p>

            {/* Tre slags fugtmåling */}
            <div>
              <h2 className="text-2xl font-extrabold text-stone-900 tracking-tight mb-4">
                Tre slags fugtmåling
              </h2>
              <div className="space-y-4">
                <div className="p-5 rounded-2xl bg-[#FAF9F6] border border-stone-200">
                  <h3 className="font-bold text-stone-900 mb-1">
                    1. Ikke-destruktiv scanning
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed">
                    Et instrument sender et svagt elektrisk signal ind i materialet. Fugt ændrer signalet, og instrumentet viser, hvor meget materialet afviger fra et tørt område. Der bores ikke, og overfladen tager ikke skade. Metoden er hurtig og egnet til at kortlægge store flader som tage, vægge og betongulve. Værdierne er sammenlignende – de viser, hvor fugten er, men er ikke et præcist tal for fugtindholdet i alle materialer.
                  </p>
                </div>

                <div className="p-5 rounded-2xl bg-[#FAF9F6] border border-stone-200">
                  <h3 className="font-bold text-stone-900 mb-1">
                    2. Nålemåling
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed">
                    I træ og gips trykkes to nåle ind i materialet, og fugtindholdet måles i procent. Metoden giver et præcist tal for træ og efterlader kun to små huller.
                  </p>
                </div>

                <div className="p-5 rounded-2xl bg-[#FAF9F6] border border-stone-200">
                  <h3 className="font-bold text-stone-900 mb-1">
                    3. RF-måling i borehuller
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed">
                    I beton kan den relative fugtighed (RF) måles ved at bore huller og sætte målere ned i betonen. Målerne skal stå en periode, før de aflæses. Det er den metode, man bruger, når der skal dokumenteres en konkret RF-værdi, fx før gulvlægning.
                  </p>
                </div>
              </div>
              <p className="text-sm text-stone-600 mt-4 italic">
                I praksis starter man ofte med den ikke-destruktive scanning for at finde ud af, hvor fugten er. Derefter vælger man, om der er brug for en mere præcis måling på udvalgte steder.
              </p>
            </div>

            {/* Hvornår giver en fugtmåling mening? */}
            <div className="pt-4">
              <h2 className="text-2xl font-extrabold text-stone-900 tracking-tight mb-4">
                Hvornår giver en fugtmåling mening?
              </h2>
              <ul className="space-y-3 text-sm text-stone-700 list-none pl-0">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span><strong>Nybyggeri:</strong> Før der lægges gulv på et betondæk eller et afretningslag, og før en konstruktion lukkes med plader.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span><strong>Vandskade:</strong> For at finde ud af, hvor langt vandet har bredt sig, og for at følge udtørringen.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span><strong>Fladt tag:</strong> For at finde ud af, om det er hele taget eller kun et område, der er vådt under tagpappen.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span><strong>Kælder og badeværelse:</strong> Når der er lugt, skjolder eller afskallende maling.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span><strong>Bolighandel:</strong> Når du er i tvivl om et konkret område, før du skriver under.</span>
                </li>
              </ul>
            </div>

            {/* Fugt eller kondens? */}
            <div className="pt-4">
              <h2 className="text-2xl font-extrabold text-stone-900 tracking-tight mb-3">
                Fugt eller kondens?
              </h2>
              <p className="text-sm text-stone-700 leading-relaxed">
                Ikke al fugt kommer udefra. Når en overflade er koldere end luftens dugpunkt, fortætter vanddampen i luften på overfladen – det er kondens. Derfor måler en god fugtmåling både materialerne og rumluften: luftfugtighed, temperatur, dugpunkt og overfladetemperatur. Så er det lettere at skelne mellem en utæthed og et indeklimaproblem.
              </p>
            </div>

            {/* Fugt og skimmel */}
            <div className="pt-4">
              <h2 className="text-2xl font-extrabold text-stone-900 tracking-tight mb-3">
                Fugt og skimmel
              </h2>
              <p className="text-sm text-stone-700 leading-relaxed">
                Fugt giver grobund for skimmel, men en fugtmåling er ikke en skimmelundersøgelse. Den kan vise, hvor der er fugt, og dermed hvor en eventuel skimmelprøve bør tages.
              </p>
            </div>

            {/* Hvad kan målingen ikke? */}
            <div className="pt-4 p-6 rounded-2xl bg-amber-50/70 border border-amber-200/80">
              <h2 className="text-xl font-bold text-amber-950 mb-3">
                Hvad kan målingen ikke?
              </h2>
              <p className="text-sm text-amber-900 leading-relaxed">
                Den ikke-destruktive scanning virker ikke gennem metal, fx stålplader eller alu-beklædning, og ikke på visse gummibaserede tagmembraner. Tagflader skal være tørre på overfladen, når der scannes. Og fordi værdierne er sammenlignende, skal resultatet altid vurderes af en, der kender konstruktionen.
              </p>
            </div>

            {/* Sådan forbereder du dig */}
            <div className="pt-4">
              <h2 className="text-2xl font-extrabold text-stone-900 tracking-tight mb-4">
                Sådan forbereder du dig
              </h2>
              <ol className="space-y-2.5 text-sm text-stone-700 pl-5 list-decimal">
                <li>
                  <strong>Skriv ned, hvad du har set, og hvornår</strong> – fx efter kraftig regn eller i vinterhalvåret.
                </li>
                <li>
                  <strong>Tag fotos</strong> af skjolder, revner eller fugtige områder.
                </li>
                <li>
                  <strong>Sørg for adgang</strong> til de rum og flader, der skal måles – og ved tagmåling sikker adgang til taget.
                </li>
              </ol>
            </div>

            {/* Sådan kan vi hjælpe */}
            <div className="pt-6 pb-2">
              <h2 className="text-2xl font-extrabold text-stone-900 tracking-tight mb-3">
                Sådan kan vi hjælpe
              </h2>
              <p className="text-sm text-stone-700 leading-relaxed">
                Hos ET Danmark måler vi fugt med professionelt Tramex-udstyr – i tage, vægge, betongulve og træ. Vi laver også termografi og trykprøvning, så fugt, kuldebroer og utætheder kan ses i sammenhæng. Med mere end 20 års erfaring i byggebranchen giver vi dig en klar vurdering af, hvad målingen viser, og hvad der bør ske nu.
              </p>
            </div>

            {/* CTA BOX */}
            <div className="mt-8 p-8 rounded-3xl bg-[#165B4C] text-white flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl">
              <div>
                <h3 className="text-2xl font-bold mb-1">
                  Har du mistanke om fugt i dit byggeri?
                </h3>
                <p className="text-emerald-100 text-sm">
                  Få en professionel undersøgelse og en officiel rapport inden for 2–5 hverdage.
                </p>
              </div>
              <Link
                to="/fugtmaaling"
                className="px-6 py-3 rounded-full bg-white text-[#165B4C] hover:bg-stone-100 font-bold text-sm shadow-md transition-colors whitespace-nowrap"
              >
                Få et uforpligtende tilbud på fugtmåling &rarr;
              </Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
